type QuestLike = {
  title?: string | null
  body?: string | null
  summary?: string | null
  excerpt?: string | null
  warning?: string | null
  pack_code?: string | null
  packCode?: string | null
  is_adult?: boolean | null
  isAdult?: boolean | null
  modes?: string[] | string | null
  meta?: any
}

type TranslationBucket = {
  title?: string
  body?: string
  summary?: string
  warning?: string
  role1_name?: string
  role2_name?: string
  role1_text?: string
  role2_text?: string
}

export type QuestVariationOption = {
  key: string
  label: string
  note?: string
}

export type ResolvedQuestContent = {
  language: string
  title: string
  body: string
  summary: string
  warning: string
  warnings: string[]
  role1Name: string
  role2Name: string
  role1Text: string
  role2Text: string
  hasRole2: boolean
  modes: string[]
  packCode: string
  isAdult: boolean
  availableVariations: QuestVariationOption[]
  variationNote: string
}

function s(v: unknown): string {
  return typeof v === "string" ? v.trim() : ""
}

function asObject(v: unknown): Record<string, any> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, any>) : {}
}

function asArray(v: unknown): any[] {
  return Array.isArray(v) ? v : []
}

function asModes(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => s(x)).filter(Boolean)
  if (typeof v === "string") {
    const raw = v.trim()
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed.map((x) => s(x)).filter(Boolean)
    } catch {}
    return raw.split(",").map((x) => x.trim()).filter(Boolean)
  }
  return []
}

function pick(...vals: unknown[]): string {
  for (const v of vals) {
    const out = s(v)
    if (out) return out
  }
  return ""
}

function normalizeLang(lang: string): string {
  return s(lang).toLowerCase() || "en"
}

function buildLangOrder(lang: string): string[] {
  const wanted = normalizeLang(lang)
  if (wanted === "en") return ["en"]
  return [wanted, "en"]
}

function translatedBucket(translations: Record<string, any>, lang: string): TranslationBucket {
  return asObject(translations[lang]) as TranslationBucket
}

function resolveStyledBody(meta: Record<string, any>, style?: string): string {
  const styleKey = s(style).toUpperCase()
  if (!styleKey || styleKey === "DEFAULT") return ""
  const styles = asObject(meta.styles)
  const styleObj = asObject(styles[styleKey])
  return pick(styleObj.body)
}

function resolveVariation(meta: Record<string, any>, variation?: string) {
  const vars = asObject(meta.variations)
  const key = s(variation)
  if (!key) return { body: "", note: "" }
  const obj = asObject(vars[key])
  return {
    body: pick(obj.body),
    note: pick(obj.note, obj.description),
  }
}

function getVariationOptions(meta: Record<string, any>): QuestVariationOption[] {
  const vars = asObject(meta.variations)
  return Object.entries(vars).map(([key, raw]) => {
    const obj = asObject(raw)
    return {
      key,
      label: pick(obj.label, key),
      note: pick(obj.note, obj.description),
    }
  })
}

function getWarnings(meta: Record<string, any>, singleWarning: string): string[] {
  const out: string[] = []
  if (singleWarning) out.push(singleWarning)

  const metaWarnings = asArray(meta.warnings)
    .map((x) => s(x))
    .filter(Boolean)

  for (const w of metaWarnings) {
    if (!out.includes(w)) out.push(w)
  }
  return out
}

export function resolveQuestContent(
  quest: QuestLike,
  language: string = "en",
  opts?: { style?: string; variation?: string }
): ResolvedQuestContent {
  const meta = asObject(quest.meta)
  const translations = asObject(meta.translations)
  const langs = buildLangOrder(language)

  const wanted = translatedBucket(translations, langs[0])
  const en = translatedBucket(translations, "en")

  const styledBody = resolveStyledBody(meta, opts?.style)
  const variationResolved = resolveVariation(meta, opts?.variation)

  const title = pick(wanted.title, en.title, quest.title)
  const body = pick(
    variationResolved.body,
    styledBody,
    wanted.body,
    en.body,
    quest.body
  )
  const summary = pick(wanted.summary, en.summary, meta.summary, quest.summary, quest.excerpt)
  const warning = pick(wanted.warning, en.warning, meta.warning, quest.warning)

  const role2Name = pick(wanted.role2_name, en.role2_name, meta.role2_name)
  const role2Text = pick(wanted.role2_text, en.role2_text, meta.role2_text)

  return {
    language: normalizeLang(language),
    title,
    body,
    summary,
    warning,
    warnings: getWarnings(meta, warning),
    role1Name: pick(wanted.role1_name, en.role1_name, meta.role1_name),
    role2Name,
    role1Text: pick(wanted.role1_text, en.role1_text, meta.role1_text),
    role2Text,
    hasRole2: !!(role2Name || role2Text),
    modes: asModes(quest.modes),
    packCode: pick(quest.packCode, quest.pack_code),
    isAdult: Boolean(quest.isAdult ?? quest.is_adult ?? meta?.csv?.is_adult ?? false),
    availableVariations: getVariationOptions(meta),
    variationNote: variationResolved.note,
  }
}
